<template>
  <div class="human-verification-root">
    <div
      v-show="state.verified"
      class="uplus-site-content"
      :aria-hidden="state.verified ? 'false' : 'true'"
    >
      <App v-if="entered" />
    </div>

    <section
      v-if="!state.verified"
      class="uplus-verification-gate"
      aria-labelledby="uplus-gate-title"
    >
      <div class="uplus-gate-card">
        <span class="uplus-gate-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
            <path d="M12 3l7 3v5c0 4.6-2.8 8.1-7 10-4.2-1.9-7-5.4-7-10V6l7-3z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
            <path d="M8.7 12.1l2.1 2.1 4.6-4.7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h1 id="uplus-gate-title">{{ text.title }}</h1>
        <p>{{ text.intro }}</p>
        <p class="uplus-gate-status" role="status">{{ statusText }}</p>
        <button
          ref="retry"
          type="button"
          :disabled="busy"
          @click="retryVerification"
        >
          {{ busy ? text.checking : text.retry }}
        </button>
        <span class="uplus-gate-footer">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
            <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.8" />
            <path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          {{ text.protected }}
        </span>
      </div>
    </section>
  </div>
</template>

<script>
import { baseUrl } from '@/utils/request'
import { readLanguage } from '@/utils/locale'
import {
  checkVerificationAccess,
  ensureHumanVerification,
  humanVerificationState,
  resumeHumanVerification
} from '@/utils/humanVerification'

const copy = {
  'zh-CN': {
    title: '安全验证',
    intro: '完成安全验证后即可进入 UPlusTrade。',
    pending: '正在准备安全验证…',
    cancelled: '验证尚未完成，请继续验证后进入网站。',
    invalid: '验证已失效，请重新验证后继续访问。',
    unavailable: '验证服务暂不可用，请稍后重试。',
    retry: '继续验证',
    checking: '正在验证…',
    protected: '安全访问 · UPlusTrade'
  },
  'zh-HK': {
    title: '安全驗證',
    intro: '完成安全驗證後即可進入 UPlusTrade。',
    pending: '正在準備安全驗證…',
    cancelled: '驗證尚未完成，請繼續驗證後進入網站。',
    invalid: '驗證已失效，請重新驗證後繼續瀏覽。',
    unavailable: '驗證服務暫不可用，請稍後重試。',
    retry: '繼續驗證',
    checking: '正在驗證…',
    protected: '安全存取 · UPlusTrade'
  },
  en: {
    title: 'Security check',
    intro: 'Complete verification to enter UPlusTrade.',
    pending: 'Preparing your security check…',
    cancelled: 'Verification is incomplete. Complete the check to enter.',
    invalid: 'Verification expired. Verify again to continue.',
    unavailable: 'Verification is unavailable. Please try again later.',
    retry: 'Continue verification',
    checking: 'Verifying…',
    protected: 'Secure access · UPlusTrade'
  },
  ko: {
    title: '보안 인증',
    intro: '보안 인증을 완료하면 UPlusTrade에 접속할 수 있습니다.',
    pending: '보안 인증을 준비하는 중…',
    cancelled: '인증이 완료되지 않았습니다. 인증을 완료해 주세요.',
    invalid: '인증이 만료되었습니다. 계속하려면 다시 인증해 주세요.',
    unavailable: '현재 인증을 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
    retry: '인증 계속하기',
    checking: '인증 중…',
    protected: '안전한 접속 · UPlusTrade'
  }
}

export default {
  name: 'HumanVerificationGate',
  components: {
    App: () => import('@/App.vue')
  },
  data() {
    return {
      state: humanVerificationState,
      entered: humanVerificationState.verified,
      locale: readLanguage(),
      controller: null
    }
  },
  computed: {
    text() {
      return copy[this.locale] || copy['zh-HK']
    },
    busy() {
      return !this.state.notice || this.state.notice.retrying
    },
    statusText() {
      if (this.busy) return this.text.pending
      if (this.state.notice.code === 'cancelled') return this.text.cancelled
      if (this.state.notice.code === 'invalid') return this.text.invalid
      return this.text.unavailable
    }
  },
  watch: {
    'state.verified'(verified) {
      if (verified) {
        this.entered = true
        return
      }
      this.$nextTick(() => {
        if (this.state.notice && !this.state.notice.retrying && this.$refs.retry) {
          this.$refs.retry.focus()
        } else if (!this.state.notice) {
          this.startVerification()
        }
      })
    },
    'state.notice'(notice) {
      if (notice && !notice.retrying) {
        this.$nextTick(() => {
          if (this.$refs.retry) this.$refs.retry.focus()
        })
      }
    }
  },
  mounted() {
    this.startVerification()
    window.addEventListener('focus', this.checkAccess)
    window.addEventListener('pageshow', this.checkAccess)
    window.addEventListener('hashchange', this.checkAccess)
    window.addEventListener('popstate', this.checkAccess)
    window.addEventListener('uplus-language-change', this.handleLanguageChange)
    document.addEventListener('visibilitychange', this.checkAccess)
  },
  beforeDestroy() {
    if (this.controller) this.controller.abort()
    window.removeEventListener('focus', this.checkAccess)
    window.removeEventListener('pageshow', this.checkAccess)
    window.removeEventListener('hashchange', this.checkAccess)
    window.removeEventListener('popstate', this.checkAccess)
    window.removeEventListener('uplus-language-change', this.handleLanguageChange)
    document.removeEventListener('visibilitychange', this.checkAccess)
  },
  methods: {
    startVerification() {
      if (this.state.verified || this.state.notice || this.controller) return
      this.controller = new AbortController()
      ensureHumanVerification(this.controller.signal, false, baseUrl)
        .catch(() => {})
        .finally(() => {
          this.controller = null
        })
    },
    retryVerification() {
      resumeHumanVerification(baseUrl)
    },
    checkAccess() {
      checkVerificationAccess(baseUrl)
    },
    handleLanguageChange(event) {
      this.locale = (event && event.detail) || readLanguage()
    }
  }
}
</script>

<style scoped>
.human-verification-root {
  min-height: 100vh;
  min-height: 100dvh;
}
.uplus-verification-gate {
  position: fixed;
  inset: 0;
  z-index: 200;
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding:
    max(20px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  color: #0a1733;
  background: radial-gradient(ellipse at 50% 0%, #e6eeff, #f5f8ff 60%);
}
.uplus-gate-card {
  width: 100%;
  max-width: 420px;
  padding: 32px 28px 24px;
  background: #fff;
  border: 1px solid #e3e9f4;
  border-radius: 20px;
  box-shadow: 0 24px 64px -32px rgba(10, 23, 51, .3);
}
.uplus-gate-mark {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  color: #1d4ed8;
  background: #eaf0fe;
  border-radius: 14px;
}
.uplus-gate-card h1 {
  margin: 20px 0 8px;
  font-size: 24px;
  line-height: 1.4;
}
.uplus-gate-card p {
  margin: 0;
  color: #5b6780;
  font-size: 14px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}
.uplus-gate-card .uplus-gate-status {
  margin: 24px 0 16px;
  padding: 12px 14px;
  background: #f5f8ff;
  border-radius: 10px;
}
.uplus-gate-card button {
  display: block;
  width: 100%;
  min-height: 46px;
  padding: 12px 18px;
  color: #fff;
  background: #1d4ed8;
  border: 0;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
}
.uplus-gate-card button:hover:not(:disabled) { background: #1736a8; }
.uplus-gate-card button:disabled { cursor: wait; opacity: .6; }
.uplus-gate-card button:focus-visible {
  outline: 3px solid #93b4ff;
  outline-offset: 3px;
}
.uplus-gate-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
  color: #65748c;
  font-size: 12px;
}
@media (max-width: 360px) {
  .uplus-gate-card {
    padding: 24px 20px 20px;
    border-radius: 16px;
  }
}
@media (max-height: 440px) {
  .uplus-verification-gate { align-items: start; }
}
</style>
